// src/hooks/useBusinesses.ts - Hook générique pour la gestion des businesses
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
  deleteField,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { Business, BusinessType, DEFAULT_BUSINESS_CONFIGURATION, BusinessConfiguration } from '../types/business';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';

// Hook pour récupérer tous les businesses (pour admin global si besoin plus tard)
export const useBusinesses = () => {
  return useQuery({
    queryKey: ['businesses'],
    queryFn: async () => {
      try {
        const q = query(
          collection(db, COLLECTIONS.businesses), 
          orderBy('name')
        );

        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useBusinesses - Main query');
          throw error;
        });

        const businesses = snapshot.docs
          .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name,
            type: data.type || 'custom',
            isActive: data.isActive,
            configurationInProgress: data.configurationInProgress ?? false,
            createdAt: data.createdAt?.toDate(),
            configuration: data.configuration || DEFAULT_BUSINESS_CONFIGURATION,
            branding: data.branding,
            activeFeatures: data.activeFeatures,
            disabledFeatures: data.disabledFeatures,
          } as Business;
          })
          // Inclure les business sans champ `isActive` (legacy) ; exclure seulement ceux explicitement désactivés.
          .filter((b) => b.isActive !== false);

        return businesses;
      } catch (error) {
        console.error('Erreur dans useBusinesses:', error);
        throw error;
      }
    },
  });
};

// Hook pour récupérer un business spécifique
export const useBusiness = (businessId: string) => {
  return useQuery({
    queryKey: ['businesses', businessId],
    queryFn: async () => {
      if (!businessId) {
        throw new Error('Business ID requis');
      }

      const docRef = doc(db, COLLECTIONS.businesses, businessId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Business introuvable');
      }

      const data = docSnap.data();
      return {
        id: docSnap.id,
        name: data.name,
        type: data.type || 'custom',
        isActive: data.isActive,
        configurationInProgress: data.configurationInProgress ?? false,
        createdAt: data.createdAt?.toDate(),
        configuration: data.configuration || DEFAULT_BUSINESS_CONFIGURATION,
        branding: data.branding,
        activeFeatures: data.activeFeatures,
        disabledFeatures: data.disabledFeatures,
      } as Business;
    },
    enabled: !!businessId,
  });
};

// Hook pour créer un business
export const useAddBusiness = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (businessData: {
      name: string;
      type: BusinessType;
      configuration?: Business['configuration'];
      branding?: Business['branding'];
    }) => {
      // Validation des données obligatoires
      if (!businessData.name.trim()) {
        throw new Error('Le nom du business est requis');
      }
      
      if (!businessData.type) {
        throw new Error('Le type de business est requis');
      }

      // Si un branding est fourni, valider les champs obligatoires
      if (businessData.branding) {
        const branding = businessData.branding;
        if (!branding.smsSenderName || !branding.phone || !branding.whatsappNumber || !branding.address) {
          throw new Error('Branding incomplet : smsSenderName, phone, whatsappNumber et address sont obligatoires');
        }
      }

      const newBusiness = {
        name: businessData.name.trim(),
        type: businessData.type,
        isActive: true,
        createdAt: Timestamp.now(),
        configuration: businessData.configuration || DEFAULT_BUSINESS_CONFIGURATION,
        // Par défaut, aucune liste activeFeatures explicite : toutes les features sont considérées actives (rétrocompatibilité)
        ...(businessData.branding && { branding: businessData.branding }),
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.businesses), newBusiness);
      
      console.log('Nouveau business créé:', { id: docRef.id, ...newBusiness });
      
      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
    },
  });
};

// Hook pour mettre à jour un business
export const useUpdateBusiness = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updateData }: Partial<Business> & { id: string }) => {
      if (!id) {
        throw new Error('ID du business requis pour la mise à jour');
      }

      // Vérifier que le business existe
      const docRef = doc(db, COLLECTIONS.businesses, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Business introuvable');
      }

      const currentBusinessData = docSnap.data() as Business;
      // Utiliser 'any' pour lire les anciens champs qui peuvent encore exister dans Firestore
      const currentConfiguration: any = currentBusinessData.configuration || DEFAULT_BUSINESS_CONFIGURATION;

      // Préparer les données de mise à jour
      const updatePayload: any = {};
      
      // Champs modifiables
      if (updateData.name !== undefined) {
        updatePayload.name = updateData.name.trim();
      }
      
      if (updateData.type !== undefined) {
        updatePayload.type = updateData.type;
      }
      
      if (updateData.isActive !== undefined) {
        updatePayload.isActive = updateData.isActive;
      }

      if (updateData.configurationInProgress !== undefined) {
        updatePayload.configurationInProgress = updateData.configurationInProgress;
      }
      
      if (updateData.configuration !== undefined) {
        // Nettoyer les anciens champs de validation des rapports d'activité (migrés au niveau employé)
        const cleanedConfiguration = { ...updateData.configuration };
        
        // S'assurer que activityReport existe dans la config nettoyée
        if (!cleanedConfiguration.activityReport) {
          cleanedConfiguration.activityReport = currentConfiguration.activityReport || {};
        }
        
        // Supprimer validationMode et weeklyDeadlineDay s'ils existent encore (migrés au niveau employé)
        // Utiliser la notation avec points au niveau supérieur pour deleteField()
        if (cleanedConfiguration.activityReport) {
          // Nettoyer les anciens champs de l'objet activityReport
          const activityReportUpdate: any = {
            ...cleanedConfiguration.activityReport,
          };
          
          // Supprimer les anciens champs de l'objet (ne pas utiliser deleteField ici)
          delete activityReportUpdate.validationMode;
          delete activityReportUpdate.weeklyDeadlineDay;
          
          cleanedConfiguration.activityReport = activityReportUpdate;
        }
        
        updatePayload.configuration = cleanedConfiguration;
        console.log('🧪 useUpdateBusiness - configuration payload', {
          businessId: id,
          messagesClients: (cleanedConfiguration as any)?.messagesClients,
        });
        
        // Supprimer les anciens champs au niveau supérieur avec deleteField() si ils existent dans Firestore
        if (currentConfiguration.activityReport?.validationMode !== undefined) {
          updatePayload['configuration.activityReport.validationMode'] = deleteField();
        }
        if (currentConfiguration.activityReport?.weeklyDeadlineDay !== undefined) {
          updatePayload['configuration.activityReport.weeklyDeadlineDay'] = deleteField();
        }
      }
      
      if (updateData.branding !== undefined) {
        // Si on met à jour le branding, valider les champs obligatoires
        if (updateData.branding) {
          const branding = updateData.branding;
          if (!branding.smsSenderName || !branding.phone || !branding.whatsappNumber || !branding.address) {
            throw new Error('Branding incomplet : smsSenderName, phone, whatsappNumber et address sont obligatoires');
          }
        }
        updatePayload.branding = updateData.branding;
      }

      if (updateData.activeFeatures !== undefined) {
        updatePayload.activeFeatures = updateData.activeFeatures;
      }

      if (updateData.disabledFeatures !== undefined) {
        updatePayload.disabledFeatures = updateData.disabledFeatures;
      }

      if (updateData.bonspotMerchantId !== undefined) {
        updatePayload.bonspotMerchantId = updateData.bonspotMerchantId;
      }

      if (updateData.loyaltyPassProgram !== undefined) {
        updatePayload.loyaltyPassProgram = updateData.loyaltyPassProgram;
      }

      // Ajouter la date de modification
      updatePayload.updatedAt = Timestamp.now();

      await updateDoc(docRef, updatePayload);
      
      console.log('Business mis à jour:', { id, ...updatePayload });
      if (updatePayload.configuration) {
        console.log('✅ useUpdateBusiness - configuration persisted', {
          businessId: id,
          persistedMessagesClients: (updatePayload.configuration as any)?.messagesClients,
        });
      }
      
      return { id, ...updateData };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      queryClient.invalidateQueries({ queryKey: ['businesses', data.id] });
      queryClient.invalidateQueries({ queryKey: ['super_admin', 'businesses'] });
      // ✅ NOUVEAU : Invalider aussi les catégories de dépenses si la configuration a été mise à jour
      if (data.configuration !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['expenseCategories', data.id] });
      }
    },
  });
};

// Hook pour désactiver un business (soft delete)
export const useDeactivateBusiness = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (businessId: string) => {
      if (!businessId) {
        throw new Error('ID du business requis');
      }

      const docRef = doc(db, COLLECTIONS.businesses, businessId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Business introuvable');
      }

      await updateDoc(docRef, {
        isActive: false,
        deactivatedAt: Timestamp.now(),
      });
      
      console.log('Business désactivé:', businessId);
      
      return businessId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
    },
  });
};

// Hook pour réactiver un business
export const useReactivateBusiness = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (businessId: string) => {
      if (!businessId) {
        throw new Error('ID du business requis');
      }

      const docRef = doc(db, COLLECTIONS.businesses, businessId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Business introuvable');
      }

      await updateDoc(docRef, {
        isActive: true,
        reactivatedAt: Timestamp.now(),
        deactivatedAt: null,
      });
      
      console.log('Business réactivé:', businessId);
      
      return businessId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
    },
  });
};

// Hook pour supprimer définitivement un business (uniquement si archivé)
export const useDeleteBusiness = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (businessId: string) => {
      if (!businessId) {
        throw new Error('ID du business requis');
      }

      const docRef = doc(db, COLLECTIONS.businesses, businessId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Business introuvable');
      }

      const businessData = docSnap.data();
      
      // Vérifier que le business est archivé (isActive: false)
      if (businessData.isActive !== false) {
        throw new Error('Impossible de supprimer un business actif. Veuillez d\'abord l\'archiver.');
      }

      // Supprimer définitivement le business
      await deleteDoc(docRef);
      
      console.log('Business supprimé définitivement:', businessId);
      
      return businessId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      queryClient.invalidateQueries({ queryKey: ['super_admin', 'businesses'] });
    },
  });
};

// Hook pour mettre à jour la configuration d'un business
export const useUpdateBusinessConfiguration = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      businessId,
      configuration,
    }: {
      businessId: string;
      configuration: Business['configuration'];
    }) => {
      if (!businessId) {
        throw new Error('ID du business requis');
      }

      const docRef = doc(db, COLLECTIONS.businesses, businessId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Business introuvable');
      }

      await updateDoc(docRef, {
        configuration: configuration || DEFAULT_BUSINESS_CONFIGURATION,
        updatedAt: Timestamp.now(),
      });
      
      console.log('Configuration mise à jour pour business:', businessId);
      
      return { businessId, configuration };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      queryClient.invalidateQueries({ queryKey: ['businesses', data.businessId] });
    },
  });
};

// Hook pour mettre à jour le branding d'un business
export const useUpdateBusinessBranding = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      businessId,
      branding,
    }: {
      businessId: string;
      branding: Business['branding'];
    }) => {
      if (!businessId) {
        throw new Error('ID du business requis');
      }

      // Valider le branding si fourni
      if (branding) {
        if (!branding.smsSenderName || !branding.phone || !branding.whatsappNumber || !branding.address) {
          throw new Error('Branding incomplet : smsSenderName, phone, whatsappNumber et address sont obligatoires');
        }
      }

      const docRef = doc(db, COLLECTIONS.businesses, businessId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Business introuvable');
      }

      await updateDoc(docRef, {
        branding,
        updatedAt: Timestamp.now(),
      });
      
      console.log('Branding mis à jour pour business:', businessId);
      
      return { businessId, branding };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      queryClient.invalidateQueries({ queryKey: ['businesses', data.businessId] });
    },
  });
};

// Hook pour obtenir les businesses par type
export const useBusinessesByType = (businessType: BusinessType) => {
  return useQuery({
    queryKey: ['businesses', 'type', businessType],
    queryFn: async () => {
      const q = query(
        collection(db, COLLECTIONS.businesses),
        where('type', '==', businessType),
        where('isActive', '==', true),
        orderBy('name')
      );
      
      const snapshot = await getDocs(q).catch(error => {
        handleFirestoreError(error, 'useBusinesses - Query');
        throw error;
      });
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          type: data.type,
          isActive: data.isActive,
          createdAt: data.createdAt?.toDate(),
          configuration: data.configuration || DEFAULT_BUSINESS_CONFIGURATION,
          branding: data.branding,
          activeFeatures: data.activeFeatures,
          disabledFeatures: data.disabledFeatures,
        } as Business;
      });
    },
    enabled: !!businessType,
  });
};